import { FULL_TIME_WORKER } from "../../../../../assets/constants";
import {
	ShiftPatternOption,
	ShiftPatternWorkCenter,
} from "../../../store/types";

interface WorkerTypeLike {
	id?: number | string;
	name?: string;
	label?: string;
}

// Worker type names arrive as "FullTime" or "Full Time" depending on the API.
// Matched by name, never by id: BE does not guarantee the Full Time id.
const isFullTimeName = (name?: string) =>
	String(name ?? "").replace(/\s+/g, "") === FULL_TIME_WORKER;

const isFullTimeType = (type: WorkerTypeLike) =>
	isFullTimeName(type.name) || isFullTimeName(type.label);

// Shift patterns belong to Full Time workers only, so the dropdown is usable
// only while the worker types in play are all Full Time. An empty selection
// means "every worker type" on the wire, which includes Flex.
export const isFullTimeOnly = (selectedWorkerTypes: WorkerTypeLike[]) =>
	selectedWorkerTypes.length > 0 && selectedWorkerTypes.every(isFullTimeType);

const parseIds = (ids: string | null | undefined) =>
	String(ids ?? "")
		.split(",")
		.map((id) => Number(id.trim()))
		.filter((id) => Number.isFinite(id) && id > 0);

// Patterns at the selected locations, narrowed to the ticked work centers
// when any are ticked. A pattern with no work-center mapping is dropped once
// work centers narrow the audience, since nothing ties it to them.
export const getVisibleShiftPatterns = ({
	shiftPatterns,
	shiftPatternWorkCenters,
	locationIds,
	workCenterIds,
}: {
	shiftPatterns: ShiftPatternOption[];
	shiftPatternWorkCenters: ShiftPatternWorkCenter[];
	locationIds: number[];
	workCenterIds: number[];
}) => {
	if (locationIds.length === 0) return [];
	const locations = new Set(locationIds);
	const workCentersByPattern = new Map<number, number[]>();
	shiftPatternWorkCenters.forEach((item) => {
		workCentersByPattern.set(item.patternId, parseIds(item.workCenterIds));
	});
	return shiftPatterns
		.filter((pattern) => pattern.isActive !== false)
		.filter(
			(pattern) =>
				pattern.locationId !== null && locations.has(pattern.locationId)
		)
		.filter((pattern) => {
			if (workCenterIds.length === 0) return true;
			const mapped = workCentersByPattern.get(pattern.id) ?? [];
			return mapped.some((id) => workCenterIds.includes(id));
		})
		.sort((a, b) => a.name.localeCompare(b.name));
};
